// Review ekranı — plan §4.5.4 (#screen-section-review).
// Sol nav: bölümler + status chip · Orta: TipTap editor · Sağ: Onayla/Reddet/Yeniden Üret.
// "Hepsini Onayla" toplu aksiyon (confirm modal — karar #6).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type {
  BulkApproveResponse,
  ProjectProvenanceResponse,
  ReviewListResponse,
  ReviewRequest,
  ReviewSection,
} from "@mahir/shared";
import { api, ApiError } from "../api/client";
import { acceptQc, rejectQc } from "../api/qc";
import { SectionEditor } from "../components/review/SectionEditor";
import { DiffView } from "../components/review/DiffView";
import { QcPanel } from "../components/review/QcPanel";
import { QcPreview } from "../components/review/QcPreview";
import { CritiquePanel } from "../components/review/CritiquePanel";
import { FinalReportPanel } from "../components/review/FinalReportPanel";
import { RejectModal, ReopenModal, BulkApproveModal } from "../components/review/ReviewModals";
import { ProvenanceBand } from "../components/review/ProvenanceBand";
import { KpiWarningsBox } from "../components/review/KpiWarningsBox";
import { KpiStaleBand } from "../components/review/KpiStaleBand";
import { ServerChangedBand } from "../components/review/ServerChangedBand";
import { RegenerateSectionButton, sectionBusy } from "../components/review/RegenerateSectionButton";
import {
  approveDisabledReason,
  bulkApproveDisabledReason,
  bulkApproveMessage,
} from "../lib/review-approve";
import { KPI_COPY } from "../lib/kpi-ui";
import { kpiWarningsStorageKey } from "../lib/kpi-warnings-ignore";
import { getProjectProvenance } from "../api/report-import";
import { useAuthStore } from "../stores/auth-store";
import { canReopenProject } from "../lib/role-ui";
import { celebrateOnce } from "../lib/celebrate";
import { ReportCheckTab } from "../components/review/ReportCheckTab";
import { ReportCheckBadge } from "../components/review/ReportCheckBadge";
import { ReportCheckSectionBlock } from "../components/review/ReportCheckSectionBlock";
import { ReportCheckTabSwitch } from "../components/review/ReportCheckTabSwitch";
import { useReportCheckReview } from "../hooks/use-report-check-review";
import {
  serverChangedWhileDirty,
  shouldAdoptServerContent,
  shouldResetLocalContent,
} from "../lib/review-content-sync";
import { REVIEW_COPY } from "../lib/review-copy";

const STATUS_LABELS: Record<string, { label: string; bg: string; fg: string }> = {
  pending_review: {
    label: "İnceleme bekliyor",
    bg: "color-mix(in srgb, var(--color-warning) 8%, white)",
    fg: "var(--color-warning)",
  },
  approved: {
    label: "Onaylandı ✓",
    bg: "color-mix(in srgb, var(--color-success) 10%, white)",
    fg: "var(--color-success)",
  },
  rejected: {
    label: "Reddedildi",
    bg: "color-mix(in srgb, var(--color-error) 10%, white)",
    fg: "var(--color-error)",
  },
};

export function Review(): JSX.Element {
  const { id = "" } = useParams();
  const [sections, setSections] = useState<ReviewSection[]>([]);
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const rc = useReportCheckReview(id, setActiveCode, activeCode, () => hasUnsavedChanges);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  // Kayıt hatası kalıcı satır içi uyarı — `loadSections`'ın `setError(null)`'u bunu silmez.
  const [saveErrorText, setSaveErrorText] = useState<string | null>(null);
  const [localContent, setLocalContent] = useState<string>("");
  const [rejectReason, setRejectReason] = useState<string>("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [viewMode, setViewMode] = useState<"edit" | "diff">("edit");
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [provenance, setProvenance] = useState<ProjectProvenanceResponse | null>(null);
  const user = useAuthStore((s) => s.user);
  const isAdmin = canReopenProject(user); // rol adına değil izne (rev. madde 12 B1)

  const loadSections = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await api<ReviewListResponse>(`/projects/${id}/review`);
      setSections(res.sections);
      if (res.sections.length > 0 && !activeCode) {
        setActiveCode(res.sections[0]!.sectionCode);
        setLocalContent(res.sections[0]!.content ?? "");
      }
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [id, activeCode]);

  useEffect(() => {
    if (!id) return;
    void loadSections();
  }, [id, loadSections]);

  // Köken (Task 15, G15) — Review form-fill store'unu İÇE AKTARMAZ; kendi dar
  // fetch'ini yapar. Hata sessiz: bant çizilmez, KK akışı etkilenmez.
  useEffect(() => {
    if (!id) return;
    let aborted = false;
    getProjectProvenance(id)
      .then((res) => {
        if (!aborted) setProvenance(res);
      })
      .catch(() => {
        if (!aborted) setProvenance(null);
      });
    return () => {
      aborted = true;
    };
  }, [id]);

  const activeSection = useMemo(
    () => sections.find((s) => s.sectionCode === activeCode) ?? null,
    [sections, activeCode],
  );

  // Yerel metin YALNIZ aktif bölüm değişince sıfırlanır (Finar 14.8). `sections` her
  // `loadSections`'ta (3 sn yoklama, SSE, KPI bandı) yenilenir; eskiden bu effect her yenilemede
  // yazılanı sunucu metniyle eziyordu. Aynı bölümde sunucu metni değişirse (KK kabulü, yeniden
  // üretim) yalnız kullanıcı yazmamışsa alınır.
  const prevSectionIdRef = useRef<string | null>(null);
  const prevServerContentRef = useRef<string>("");
  const localContentRef = useRef(localContent);
  localContentRef.current = localContent;
  // Final review I2 — kirli editörde sunucu metni değişti → "Sunucu metnini al / Benimkini koru"
  const [serverDiverged, setServerDiverged] = useState(false);
  useEffect(() => {
    if (!activeSection) return;
    const serverContent = activeSection.content ?? "";
    if (
      shouldResetLocalContent(
        { sectionId: prevSectionIdRef.current },
        { sectionId: activeSection.id },
      )
    ) {
      prevSectionIdRef.current = activeSection.id;
      prevServerContentRef.current = serverContent;
      setLocalContent(serverContent);
      setServerDiverged(false);
      setSaveStatus("idle");
      setSaveErrorText(null);
      return;
    }
    const prevServer = prevServerContentRef.current;
    prevServerContentRef.current = serverContent;
    const local = localContentRef.current;
    if (serverChangedWhileDirty(prevServer, serverContent, local)) setServerDiverged(true);
    else if (local === serverContent || shouldAdoptServerContent(prevServer, serverContent, local))
      setServerDiverged(false);
    setLocalContent((cur) =>
      shouldAdoptServerContent(prevServer, serverContent, cur) ? serverContent : cur,
    );
  }, [activeSection]);

  // loadSections'ı ref'te tut — SSE aboneliği activeCode değişiminde bağlantıyı
  // yeniden kurmasın (proje başına tek kalıcı EventSource).
  const loadSectionsRef = useRef(loadSections);
  useEffect(() => {
    loadSectionsRef.current = loadSections;
  }, [loadSections]);

  // KK canlı güncelleme — generation SSE kanalı (qc event'i backend yayınlar, spec §5).
  // GenerateProgress ile aynı kanal/isim ("generation"); Review ayrı route olduğundan
  // bu sayfanın TEK bağlantısı. type:"qc" → loadSections ile section rozetini tazele.
  useEffect(() => {
    if (!id) return;
    const es = new EventSource(`/api/v1/projects/${id}/generate/events`, {
      withCredentials: true,
    });
    const onGeneration = (ev: MessageEvent<string>): void => {
      try {
        const event = JSON.parse(ev.data) as { type?: string };
        if (event.type === "qc") void loadSectionsRef.current();
      } catch {
        // bozuk payload — yoksay
      }
    };
    es.addEventListener("generation", onGeneration as EventListener);
    return () => {
      es.removeEventListener("generation", onGeneration as EventListener);
      es.close();
    };
  }, [id]);

  // KK aktifken 3sn polling (spec §5 SSE fallback kuralı) — terminal state'te (ready/
  // failed/null) durur: anyBusy false olunca interval kurulmaz.
  useEffect(() => {
    // M17 T3: KPI bandından başlatılan bölüm yeniden üretimi de (generationStatus) bitene dek
    const busy = (v: string | null): boolean => v === "queued" || v === "running";
    const anyBusy = sections.some((s) => busy(s.qcStatus) || busy(s.generationStatus));
    if (!anyBusy) return;
    const t = setInterval(() => void loadSections(), 3000);
    return () => clearInterval(t);
  }, [sections, loadSections]);

  const pendingCount = sections.filter(
    (s) => s.reviewStatus === "pending_review" && s.content,
  ).length;
  const approvedCount = sections.filter((s) => s.reviewStatus === "approved").length;
  const totalCount = sections.length;
  // D7: bir bölümde bekleyen rewrite adayı varken toplu onay backend'de tüm batch'i
  // 409'lar (accept/reject beklenir) — butonu kilitle, önce adaylara karar verilsin.
  const anyRewriteReady = sections.some(
    (s) => s.qcStatus === "ready" && s.qcMeta?.promptType === "rewrite",
  );

  const saveContent = async (): Promise<void> => {
    if (!activeSection) return;
    if (!hasUnsavedChanges) return;
    setSaveStatus("saving");
    setSaveErrorText(null);
    try {
      await api(`/projects/${id}/sections/${activeSection.sectionCode}/content`, {
        method: "PATCH",
        body: { content: localContent },
      });
      setSaveStatus("saved");
      await loadSections();
    } catch (err: unknown) {
      setSaveStatus("error");
      if (err instanceof ApiError) {
        setError(err.message);
        setSaveErrorText(
          err.status === 409
            ? REVIEW_COPY.saveConflict
            : `${REVIEW_COPY.saveFailed} (${err.message})`,
        );
      } else {
        setError("Kaydedilemedi");
        setSaveErrorText(REVIEW_COPY.saveFailed);
      }
    }
  };

  const doReview = async (action: "approve" | "reject", note?: string): Promise<void> => {
    if (!activeSection) return;
    setActionLoading(true);
    try {
      const body: ReviewRequest = action === "reject" && note ? { action, note } : { action };
      await api(`/projects/${id}/sections/${activeSection.sectionCode}/review`, {
        method: "PATCH",
        body,
      });
      await loadSections();
      // M9-S4: ilk approve celebrate
      if (action === "approve") celebrateOnce("first-approve");
      setRejectReason("");
      setRejectOpen(false);
    } catch (err: unknown) {
      if (err instanceof ApiError) setError(err.message);
      else setError("İşlem başarısız");
    } finally {
      setActionLoading(false);
    }
  };

  const doReopen = async (): Promise<void> => {
    setActionLoading(true);
    try {
      const res = await api<{ reopenedSectionCount: number; supersededDocumentCount: number }>(
        `/projects/${id}/reopen`,
        { method: "POST", body: { reason: reopenReason.trim() } },
      );
      await loadSections();
      setReopenOpen(false);
      setReopenReason("");
      // #015: production-ready toast — teknik enum yerine kullanıcı dili
      setError(
        `Proje yeniden incelemeye açıldı. ${res.reopenedSectionCount.toString()} bölümün onayı kaldırıldı, mevcut rapor arşivlendi.`,
      );
    } catch (err: unknown) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Reopen başarısız");
    } finally {
      setActionLoading(false);
    }
  };

  const bulkApprove = async (): Promise<void> => {
    setActionLoading(true);
    try {
      const res = await api<BulkApproveResponse>(`/projects/${id}/review/bulk-approve`, {
        method: "POST",
        body: {},
      });
      await loadSections();
      setBulkModalOpen(false);
      setError(bulkApproveMessage(res));
    } catch (err: unknown) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Toplu onay başarısız");
    } finally {
      setActionLoading(false);
    }
  };

  // KK rewrite adayını kabul et — spec §7.2. 409 qc_stale (Task 8 hash guard) durumunda
  // backend kullanıcı-dostu Türkçe mesaj döner; her iki yolda da loadSections ile güncel
  // durum tazelenir (reviewer artık geçersiz adayı görmeye devam etmesin).
  const doQcAccept = async (): Promise<void> => {
    setActionLoading(true);
    try {
      await acceptQc(id, activeSection!.sectionCode);
      await loadSections();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kabul başarısız");
      await loadSections();
    } finally {
      setActionLoading(false);
    }
  };

  const doQcReject = async (reason?: string): Promise<void> => {
    setActionLoading(true);
    try {
      await rejectQc(id, activeSection!.sectionCode, reason);
      await loadSections();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Reddetme başarısız");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && sections.length === 0) {
    return (
      <div className="p-8" style={{ color: "var(--color-text-2)" }}>
        Yükleniyor…
      </div>
    );
  }
  if (sections.length === 0) {
    return (
      <div className="p-8">
        <p style={{ color: "var(--color-text-2)" }}>
          Henüz AI üretimi yapılmamış. Önce proje detayından "AI Üretimi Başlat".
        </p>
        <Link
          to={`/projects/${id}`}
          className="inline-block mt-4 text-sm"
          style={{ color: "var(--color-primary)" }}
        >
          ← Proje detayına dön
        </Link>
      </div>
    );
  }

  const isApproved = activeSection?.reviewStatus === "approved";
  const hasUnsavedChanges =
    activeSection !== null && localContent !== (activeSection?.content ?? "");
  // Ready-rewrite: orta panelde QcPreview mount edilir; editör + Onayla/Kaydet kilitlenir
  // (önce KK adayına karar verilmeli).
  const qcRewriteReady =
    activeSection?.qcStatus === "ready" && activeSection?.qcMeta?.promptType === "rewrite";
  const bulkReason = bulkApproveDisabledReason(sections, anyRewriteReady);
  const approveReason = activeSection
    ? approveDisabledReason(activeSection, hasUnsavedChanges, qcRewriteReady)
    : null;
  // Critique ready: editör AÇIK kalır (advisory), sorun listesi editörün üstünde gösterilir.
  // "Kapat" → doQcReject ile critique state temizlenir (accept yok).
  const qcCritiqueReady =
    activeSection?.qcStatus === "ready" && activeSection?.qcMeta?.promptType === "critique";

  return (
    <div
      className="flex h-screen"
      style={{ background: "var(--color-bg)", fontFamily: "var(--font-sans)" }}
    >
      {/* Sol nav */}
      <aside
        className="overflow-y-auto"
        style={{
          width: 280,
          borderRight: "1px solid var(--color-border)",
          background: "var(--color-surface)",
        }}
      >
        <div className="p-4">
          <Link
            to={`/projects/${id}`}
            className="text-xs"
            style={{ color: "var(--color-primary)" }}
          >
            ← Proje detayı
          </Link>
          <h1
            className="mt-2 mb-1"
            style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem" }}
          >
            Bölüm Review
          </h1>
          <ReportCheckTabSwitch tab={rc.tab} onChange={rc.setTab} />
          <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
            {approvedCount.toString()}/{totalCount.toString()} onaylı · v
            {sections[0]?.version.toString() ?? "1"}
          </p>
          <button
            onClick={() => setBulkModalOpen(true)}
            disabled={pendingCount === 0 || bulkReason !== null}
            title={bulkReason ?? undefined}
            className="mt-3 w-full px-3 py-2 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontWeight: 600,
            }}
          >
            Hepsini Onayla ({pendingCount.toString()})
          </button>
        </div>
        <ul>
          {sections.map((s) => {
            const status = STATUS_LABELS[s.reviewStatus] ?? STATUS_LABELS.pending_review!;
            const active = s.sectionCode === activeCode;
            return (
              <li key={s.id}>
                <button
                  onClick={() => rc.onSelectSection(s.sectionCode)}
                  className="w-full text-left px-4 py-3"
                  style={{
                    background: active ? "var(--color-bg)" : "transparent",
                    borderLeft: active ? "3px solid var(--color-primary)" : "3px solid transparent",
                    cursor: "pointer",
                  }}
                >
                  <div
                    className="flex items-center justify-between gap-2"
                    style={{ fontFamily: "var(--font-mono)", fontSize: "0.78rem" }}
                  >
                    <span style={{ color: "var(--color-text)", fontWeight: 600 }}>
                      {s.sectionCode}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <ReportCheckBadge code={s.sectionCode} />
                      {s.kpiStale && (
                        <span
                          style={{
                            color: "var(--color-warning-text)",
                            fontSize: "0.65rem",
                            fontWeight: 600,
                          }}
                        >
                          <span aria-hidden="true">● {KPI_COPY.review.chip}</span>
                          <span className="sr-only">{KPI_COPY.review.chipAria}</span>
                        </span>
                      )}
                      <span
                        className="px-1.5 py-0.5 rounded text-xxs"
                        style={{
                          background: status.bg,
                          color: status.fg,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                        }}
                      >
                        {status.label}
                      </span>
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* Orta + sağ */}
      <main className="flex-1 flex overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6">
          {rc.tab === "consistency" && (
            <ReportCheckTab projectId={id} onOpenSection={rc.onOpenSection} />
          )}
          {rc.tab === "sections" && activeSection && (
            <>
              <div className="mb-4 flex justify-between items-start gap-4">
                <div>
                  <h2
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: "1.5rem",
                      color: "var(--color-text)",
                    }}
                  >
                    {activeSection.sectionCode}
                  </h2>
                  <p className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
                    {isApproved
                      ? "Onaylanmış — read-only. Edit için reopen yapın."
                      : "Edit edip Kaydet'e basın. Onayla/Reddet sağda."}
                  </p>
                </div>
                {!isApproved && (
                  <button
                    onClick={() => void saveContent()}
                    disabled={!hasUnsavedChanges || saveStatus === "saving" || qcRewriteReady}
                    title={
                      qcRewriteReady
                        ? "Önce KK adayına karar verin"
                        : !hasUnsavedChanges
                          ? REVIEW_COPY.nothingToSave
                          : undefined
                    }
                    className="px-3 py-1.5 rounded-md text-xs disabled:opacity-50"
                    style={{
                      background: "var(--color-primary)",
                      color: "var(--color-surface)",
                      fontWeight: 600,
                    }}
                  >
                    {saveStatus === "saving" ? "Kaydediliyor…" : "Kaydet"}
                  </button>
                )}
                {saveStatus === "saved" && !hasUnsavedChanges && (
                  <span className="text-xs" style={{ color: "var(--color-success)" }}>
                    Kaydedildi ✓
                  </span>
                )}
              </div>
              {saveStatus === "error" && saveErrorText && (
                <div
                  role="alert"
                  data-testid="review-save-error"
                  className="mb-3 px-3 py-2 rounded-md text-xs"
                  style={{
                    background: "var(--color-warning-bg)",
                    color: "var(--color-warning-text)",
                    border: "1px solid var(--color-warning-border)",
                  }}
                >
                  {saveErrorText}
                </div>
              )}
              {/* Diff view toggle — yalnızca contentOriginal varsa; ready-rewrite'ta gizli
                  (QcPreview kendi sekmelerini gösterir, dıştaki toggle işlevsiz kalırdı). */}
              {activeSection.contentOriginal && !qcRewriteReady && (
                <div className="mb-3 flex gap-1">
                  <button
                    onClick={() => setViewMode("edit")}
                    className="px-3 py-1 rounded-t text-xs"
                    style={{
                      background: viewMode === "edit" ? "var(--color-surface)" : "transparent",
                      color: viewMode === "edit" ? "var(--color-text)" : "var(--color-text-3)",
                      borderBottom: viewMode === "edit" ? "none" : "1px solid var(--color-border)",
                      fontWeight: 600,
                    }}
                  >
                    Düzenle
                  </button>
                  <button
                    onClick={() => setViewMode("diff")}
                    className="px-3 py-1 rounded-t text-xs"
                    style={{
                      background: viewMode === "diff" ? "var(--color-surface)" : "transparent",
                      color: viewMode === "diff" ? "var(--color-text)" : "var(--color-text-3)",
                      borderBottom: viewMode === "diff" ? "none" : "1px solid var(--color-border)",
                      fontWeight: 600,
                    }}
                  >
                    Orijinal ↔ Düzenlenmiş
                  </button>
                </div>
              )}
              {/* M17 T3 — kullandığı KPI değerleri üretimden sonra değişti (ilk kutu) */}
              {serverDiverged && hasUnsavedChanges && (
                <ServerChangedBand
                  onTake={() => {
                    setLocalContent(activeSection.content ?? "");
                    setServerDiverged(false);
                  }}
                  onKeep={() => setServerDiverged(false)}
                />
              )}
              <KpiStaleBand
                key={activeSection.sectionCode}
                projectId={id}
                section={activeSection}
                hasUnsavedChanges={hasUnsavedChanges}
                onChanged={() => void loadSections()}
              />
              {/* Onarım turu — eksik veriler otomatik yerleştirildiyse bilgi rozeti */}
              {activeSection.repairMeta?.accepted && (
                <div
                  className="mb-3 rounded-md p-2.5 text-xs"
                  style={{
                    background: "color-mix(in srgb, var(--color-success) 8%, white)",
                    border: "1px solid color-mix(in srgb, var(--color-success) 30%, white)",
                    color: "var(--color-text-2)",
                  }}
                >
                  <strong style={{ color: "var(--color-success)" }}>✓ Onarım turu uygulandı</strong>{" "}
                  — yanıtlardaki eksik somut veriler otomatik yerleştirildi (kapsam %
                  {Math.round((activeSection.repairMeta.ratioBefore ?? 0) * 100)} → %
                  {Math.round((activeSection.repairMeta.ratioAfter ?? 0) * 100)}).
                </div>
              )}
              {/* Coverage guard — yanıttaki somut bilgilerin bir kısmı çıktıya yansımamış */}
              {activeSection.coverageRatio != null && activeSection.coverageRatio < 0.7 && (
                <div
                  className="mb-3 rounded-md p-3 text-sm"
                  style={{
                    background: "color-mix(in srgb, var(--color-warning) 10%, white)",
                    border: "1px solid color-mix(in srgb, var(--color-warning) 35%, white)",
                    color: "var(--color-text)",
                  }}
                >
                  <strong style={{ color: "var(--color-warning)" }}>⚠ Eksik yansıma uyarısı</strong>{" "}
                  — Form yanıtlarındaki somut bilgilerin yalnızca %
                  {Math.round(activeSection.coverageRatio * 100)}&apos;i metinde tespit edildi.
                  Aşağıdaki ifadeler çıktıda bulunamadı; gerekiyorsa metne ekleyin veya yeniden
                  üretin:
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {activeSection.coverageMissing.slice(0, 12).map((m) => (
                      <span
                        key={m}
                        className="rounded px-2 py-0.5 text-xs"
                        style={{
                          background: "var(--color-surface)",
                          border: "1px solid var(--color-border)",
                          fontFamily: "var(--font-mono)",
                        }}
                      >
                        {m}
                      </span>
                    ))}
                    {activeSection.coverageMissing.length > 12 && (
                      <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
                        +{activeSection.coverageMissing.length - 12} daha
                      </span>
                    )}
                  </div>
                </div>
              )}
              {/* T13 — KPI tablosuyla sayı tutarlılığı (projede KPI seti varsa) */}
              <KpiWarningsBox
                warnings={activeSection.kpiWarnings}
                // güvenlik/UX taraması 2026-10-08 (S-L1): "yoksay" anahtarı kullanıcı bazlı
                storageKey={
                  id && user
                    ? kpiWarningsStorageKey(user.id, id, activeSection.sectionCode)
                    : undefined
                }
                onLocate={(w) => {
                  const el = document.querySelector(".ProseMirror");
                  if (!(el instanceof HTMLElement)) return;
                  const sel = window.getSelection();
                  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
                  let node: Node | null = walker.nextNode();
                  while (node) {
                    const idx = node.textContent?.indexOf(w.raw) ?? -1;
                    if (idx >= 0) {
                      const range = document.createRange();
                      range.setStart(node, idx);
                      range.setEnd(node, idx + w.raw.length);
                      sel?.removeAllRanges();
                      sel?.addRange(range);
                      (node.parentElement ?? el).scrollIntoView({
                        block: "center",
                        behavior: "smooth",
                      });
                      return;
                    }
                    node = walker.nextNode();
                  }
                }}
              />
              {/* Köken bandı (Task 15, 10A) — bu bölüm rapordan aktarıldıysa nötr
                  tek satır. `<select>` İÇERMEZ (qc-flow.spec.ts:34). */}
              <ProvenanceBand provenance={provenance} sectionCode={activeSection.sectionCode} />
              {/* Critique sorun listesi — editörün üstünde, canlı quote doğrulaması
                  localContent'e karşı. Editör kilitlenmez (advisory); Kapat → doQcReject. */}
              {qcCritiqueReady && (
                <CritiquePanel
                  section={activeSection}
                  editorContent={localContent}
                  busy={actionLoading}
                  onClose={() => void doQcReject()}
                />
              )}
              {qcRewriteReady ? (
                <QcPreview
                  section={activeSection}
                  busy={actionLoading}
                  hasUnsavedChanges={hasUnsavedChanges}
                  onDiscardLocal={() => setLocalContent(activeSection.content ?? "")}
                  onAccept={() => void doQcAccept()}
                  onReject={(reason) => void doQcReject(reason)}
                />
              ) : viewMode === "edit" ? (
                <SectionEditor
                  content={localContent}
                  readOnly={isApproved || sectionBusy(activeSection)}
                  onChange={setLocalContent}
                />
              ) : (
                <DiffView original={activeSection.contentOriginal ?? ""} current={localContent} />
              )}
              {/* M11 — Bu section'da LLM'e gönderilen kanıt belgeler */}
              {activeSection.attachmentsUsed && activeSection.attachmentsUsed.length > 0 && (
                <div
                  className="mt-4 rounded-md p-3"
                  style={{
                    background: "var(--color-bg)",
                    border: "1px solid var(--color-border)",
                  }}
                >
                  <div
                    className="text-xs uppercase tracking-wider mb-2"
                    style={{ color: "var(--color-text-3)" }}
                  >
                    📎 AI Üretiminde Kullanılan Kanıt Belgeler
                  </div>
                  <ul className="text-sm flex flex-col gap-1.5">
                    {activeSection.attachmentsUsed.map((a) => (
                      <li key={a.attachmentId} className="flex items-center gap-2 flex-wrap">
                        <strong style={{ fontFamily: "var(--font-mono)" }}>{a.filename}</strong>
                        <span style={{ color: "var(--color-text-3)", fontSize: "0.8rem" }}>
                          · {a.questionCode} · {Math.round(a.sizeBytes / 1024).toString()} KB
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p
                    className="text-xs mt-2"
                    style={{ color: "var(--color-text-3)", fontStyle: "italic" }}
                  >
                    Raporda spesifik veri (sertifika no, tarih, ölçüm) varsa kaynak doğrulamak için
                    bu belgelere bakın.
                  </p>
                </div>
              )}
              {activeSection.reviewNote && (
                <div
                  className="mt-4 p-3 rounded-md text-sm"
                  style={{
                    background: "color-mix(in srgb, var(--color-error) 10%, white)",
                    border: "1px solid rgba(198,40,40,0.2)",
                    color: "var(--color-error)",
                  }}
                >
                  <strong>Red sebebi:</strong> {activeSection.reviewNote}
                </div>
              )}
            </>
          )}
          {error && (
            <div
              className="mt-4 p-3 rounded-md text-sm"
              style={{
                background: error.includes("onaylandı")
                  ? "color-mix(in srgb, var(--color-success) 10%, white)"
                  : "color-mix(in srgb, var(--color-error) 10%, white)",
                color: error.includes("onaylandı") ? "var(--color-success)" : "var(--color-error)",
              }}
            >
              {error}
            </div>
          )}
        </div>
        {/* Sağ rail — aksiyonlar */}
        <aside
          className="overflow-y-auto p-4"
          style={{
            width: 260,
            borderLeft: "1px solid var(--color-border)",
            background: "var(--color-surface)",
          }}
        >
          <h3
            className="mb-3 text-xs uppercase tracking-wider"
            style={{ color: "var(--color-text-3)" }}
          >
            Aksiyonlar
          </h3>
          {activeSection && (
            <ReportCheckSectionBlock
              code={activeSection.sectionCode}
              onShowAll={() => rc.setTab("consistency")}
            />
          )}
          {activeSection && !isApproved && (
            <>
              <button
                onClick={() => void doReview("approve")}
                disabled={actionLoading || approveReason !== null}
                title={approveReason ?? undefined}
                className="w-full px-3 py-2 rounded-md text-sm mb-2 disabled:opacity-50"
                style={{
                  background: "var(--color-success)",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                Onayla
              </button>
              <button
                onClick={() => setRejectOpen(true)}
                disabled={actionLoading}
                className="w-full px-3 py-2 rounded-md text-sm mb-2"
                style={{
                  background: "transparent",
                  border: "1px solid var(--color-error)",
                  color: "var(--color-error)",
                }}
              >
                Reddet…
              </button>
              {/* KK paneli — Reddet'ten sonra (spec §7.1). onStarted → loadSections + polling.
                  key: bölüm değişince remount → local error/busy state sıfırlanır (bayat hata
                  mesajı bir sonraki bölüme sızmasın). */}
              <QcPanel
                key={activeSection.sectionCode}
                projectId={id}
                section={activeSection}
                hasUnsavedChanges={hasUnsavedChanges}
                onStarted={() => {
                  void loadSections();
                }}
              />
            </>
          )}
          <RegenerateSectionButton
            key={activeSection?.sectionCode}
            projectId={id}
            section={activeSection}
            hasUnsavedChanges={hasUnsavedChanges}
            onStarted={() => void loadSections()}
          />
          {isAdmin && approvedCount > 0 && (
            <button
              onClick={() => setReopenOpen(true)}
              disabled={actionLoading}
              className="w-full px-3 py-2 rounded-md text-sm mb-2"
              style={{
                background: "transparent",
                border: "1px solid var(--color-border)",
                color: "var(--color-text-2)",
              }}
            >
              🔓 Projeyi Reopen Et (admin)
            </button>
          )}
          {activeSection?.assembledPrompt && (
            <details className="mt-6">
              <summary className="cursor-pointer text-xs" style={{ color: "var(--color-text-3)" }}>
                🔍 Bu bölüm hangi prompt'la üretildi?
              </summary>
              <pre
                className="mt-2 p-2 rounded text-xxs overflow-auto"
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "0.68rem",
                  background: "var(--color-bg)",
                  maxHeight: 400,
                  whiteSpace: "pre-wrap",
                  color: "var(--color-text-2)",
                }}
              >
                {activeSection.assembledPrompt}
              </pre>
            </details>
          )}
          {/* Nihai rapor — tüm bölümler onaylandığında DOCX üret/indir (mevcut M7 akışı
              reuse). projects.download_docx yetkisi yoksa panel kendini gizler. */}
          <FinalReportPanel projectId={id} approvedCount={approvedCount} totalCount={totalCount} />
        </aside>
      </main>

      <RejectModal
        open={rejectOpen}
        reason={rejectReason}
        loading={actionLoading}
        onReasonChange={setRejectReason}
        onDismiss={() => setRejectOpen(false)}
        onCancel={() => {
          setRejectOpen(false);
          setRejectReason("");
        }}
        onConfirm={() => void doReview("reject", rejectReason.trim())}
      />
      <ReopenModal
        open={reopenOpen}
        reason={reopenReason}
        loading={actionLoading}
        approvedCount={approvedCount}
        onReasonChange={setReopenReason}
        onDismiss={() => setReopenOpen(false)}
        onCancel={() => {
          setReopenOpen(false);
          setReopenReason("");
        }}
        onConfirm={() => void doReopen()}
      />
      <BulkApproveModal
        open={bulkModalOpen}
        pendingCount={pendingCount}
        loading={actionLoading}
        onCancel={() => setBulkModalOpen(false)}
        onConfirm={() => void bulkApprove()}
      />
      {rc.confirmNode}
    </div>
  );
}
